import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import type {
  PartFileViewerPageData,
  ListCustomInterrogationsParams,
  CustomInterrogationListItem,
  InterrogationRequestBody,
  LegacyFileTaskStatusData,
} from '@frontend/api';

import { stockInterrogatingList } from 'modules/common/forms/materialWidget/data/actions';
import {
  cadViewer,
  featureSelection,
  manufacturabilityFeedbackSelection,
} from 'modules/common/viewer/cadViewer/data/actions';
import { geometricAnalysisQueue } from 'modules/common/viewer/cadViewer/data/geometricAnalysisData/actions';
import { partFileStatuses } from 'modules/viewerPage/data/actions';
import {
  getActiveFileUUID,
  getActivePartFile,
} from 'modules/viewerPage/data/reducer';

import partLibraryService from 'services/partLibraryService';

import type { ActionOfType } from 'store/types';

import {
  findByDocumentHash,
  findByInterrogationFileId,
  pickDefault,
  toLatentSelection,
} from '../utils/interrogationSelection';
import {
  types,
  geometricInterrogation,
  type GeometricInterrogationAction,
  type ReceiveQueryPayload,
} from './actions';
import { getQuery, getSelectedResult } from './reducer';
import type {
  InterrogationSelection,
  InterrogationMaterialQuery,
} from './types';

export default function* root() {
  yield takeLatest(types.CLEAR_INTERROGATION, watchClear);
  yield takeLatest(types.RECEIVE_QUERY, watchReceiveQuery);
  yield takeEvery(types.RECEIVE_RUN_STATUS, watchReceiveRunStatus);
  yield takeLatest(
    types.SET_WITH_INTERROGATION_FILE,
    watchSetWithInterrogationFile,
  );
  yield takeLatest(types.SUBMIT_INTERROGATION, watchSubmit);
}

function* watchClear() {
  yield put(cadViewer.setGeometricEntities({}));
}

/**
 * Record the selection, then clear viewer highlights. SUBMIT_INTERROGATION and
 * CLEAR_INTERROGATION do not use this: a new run leaves highlights in place,
 * and CLEAR_INTERROGATION resets the viewer itself.
 */
function* commitSelection(
  result: InterrogationSelection | null,
  showAdditionalKeysExplicit?: string[],
) {
  yield put(geometricInterrogation.selectInterrogation({ result }));
  yield put(cadViewer.setGeometricEntities({ showAdditionalKeysExplicit }));
}

function* watchReceiveQuery(
  action: ActionOfType<
    GeometricInterrogationAction,
    typeof types.RECEIVE_QUERY
  >,
) {
  const { material, task, shouldInit, useSelectedTask }: ReceiveQueryPayload =
    action.payload;

  let useTask = task;
  if (useSelectedTask) {
    const selectedResult: InterrogationSelection | null = yield select(
      getSelectedResult,
    );
    useTask = (selectedResult || {}).task || task;
  }

  try {
    const params: ListCustomInterrogationsParams = { task: useTask };
    if (material) {
      if (material[0]) params.materialClassId = material[0];
      if (material[1]) params.materialFamilyId = material[1];
      if (material[2]) params.materialId = material[2];
    }
    const uuid: string = yield select(getActiveFileUUID);
    const results: CustomInterrogationListItem[] = yield call(
      partLibraryService.listCustomInterrogations,
      {
        uuid,
        params,
      },
    );
    yield put(geometricInterrogation.receiveSearchResults({ results }));

    if (shouldInit) {
      yield call(collectDefaultState, results);
    } else {
      yield call(collectLatentState, results);
    }
  } catch (error) {
    console.error(error);
  }
}

/**
 * A finished run replaced the part file's interrogation file, features and
 * feedback (their ids are regenerated), so refetch them and re-resolve which
 * interrogation file is selected. A failed run cleared the previous results,
 * so it refetches too.
 */
function* watchReceiveRunStatus(
  action: ActionOfType<
    GeometricInterrogationAction,
    typeof types.RECEIVE_RUN_STATUS
  >,
) {
  const { partFileUuid, status } = action.payload;
  if (status === 'running') return;

  yield put(geometricAnalysisQueue.addToStart({ partFileUuid }));

  const activeFileUuid: ReturnType<typeof getActiveFileUUID> = yield select(
    getActiveFileUUID,
  );
  if (partFileUuid !== activeFileUuid) return;

  // Ids on the active file were just regenerated, so the checked features and
  // feedback still point at rows that no longer exist.
  yield all([
    put(featureSelection.receive({ features: [] })),
    put(
      manufacturabilityFeedbackSelection.receive({
        manufacturabilityFeedback: [],
      }),
    ),
  ]);

  const query: ReturnType<typeof getQuery> = yield select(getQuery);
  const selectedResult: InterrogationSelection | null = yield select(
    getSelectedResult,
  );
  yield put(
    geometricInterrogation.receiveQuery({
      material: query.material,
      task: query.task,
      // Nothing selected yet (e.g. the first run after a mapping): pick the
      // default rather than matching against a selection that does not exist.
      shouldInit: !selectedResult,
      useSelectedTask: true,
    }),
  );
}

function* collectDefaultState(results: CustomInterrogationListItem[]) {
  yield call(commitSelection, pickDefault(results));
}

function* collectLatentState(results: CustomInterrogationListItem[]) {
  const selectedResult: InterrogationSelection | null = yield select(
    getSelectedResult,
  );
  if (!selectedResult) return;

  yield call(
    commitSelection,
    findByDocumentHash(results, selectedResult.documentHash),
  );
}

function* watchSetWithInterrogationFile(
  action: ActionOfType<
    GeometricInterrogationAction,
    typeof types.SET_WITH_INTERROGATION_FILE
  >,
) {
  const { payload } = action;
  const { partFileUuid, partInterrogationFile, showAdditionalKeysExplicit } =
    payload;

  const { task } = partInterrogationFile;

  // try to collect the result from a custom interrogation. if none exist, show a
  // latent selection with features and no associated interrogation row
  const params: ListCustomInterrogationsParams = { task };
  const results: CustomInterrogationListItem[] = yield call(
    partLibraryService.listCustomInterrogations,
    {
      uuid: partFileUuid,
      params,
    },
  );
  yield put(geometricInterrogation.receiveSearchResults({ results }));

  const result =
    findByInterrogationFileId(results, partInterrogationFile.id) ??
    toLatentSelection(partInterrogationFile);

  yield call(commitSelection, result, showAdditionalKeysExplicit);
}

function* watchSubmit(
  action: ActionOfType<
    GeometricInterrogationAction,
    typeof types.SUBMIT_INTERROGATION
  >,
) {
  try {
    const { result } = action.payload;
    const { id: customInterrogationId, interrogationFileId, task } = result;

    const activePartFile: PartFileViewerPageData = yield select(
      getActivePartFile,
    );
    const { forbiddenInterrogationInformation, isDerivedFromInterrogation } =
      activePartFile;
    const forbiddenMessage = (
      forbiddenInterrogationInformation.find((info) => info.task === task) || {}
    ).forbiddenMessage;

    if (forbiddenMessage || isDerivedFromInterrogation || interrogationFileId) {
      yield put(cadViewer.setGeometricEntities({}));
    } else {
      yield call(interrogationRequest, {
        activePartFile,
        customInterrogationId,
        task,
      });
    }
  } catch (error) {
    console.error('There was an error during interrogation', error);
  }
}

type InterrogationRequestSagaArgs = {
  activePartFile: PartFileViewerPageData;
  customInterrogationId: number | null;
  task: string;
};

function* interrogationRequest({
  activePartFile,
  customInterrogationId,
  task,
}: InterrogationRequestSagaArgs) {
  // interrogation request
  try {
    const query: {
      material: InterrogationMaterialQuery | null;
      task: string | null;
    } = yield select(getQuery);
    const { material } = query;
    // material query is [classId, familyId, materialId]; only send when fully selected
    const materialId =
      material && material.length === 3 ? material[2] ?? null : null;
    const data: InterrogationRequestBody = {
      customInterrogationId,
      materialId,
      task,
    };
    const response: Record<string, LegacyFileTaskStatusData> = yield call(
      partLibraryService.interrogationRequest,
      {
        uuid: activePartFile.uuid,
        data,
      },
    );
    const nextInterrogationStatuses = {
      [activePartFile.uuid]: response,
    };
    yield put(
      partFileStatuses.receiveInterrogationStatuses({
        interrogationStatuses: nextInterrogationStatuses,
      }),
    );
    yield put(stockInterrogatingList.add({ uuid: activePartFile.uuid }));
  } catch (error) {
    console.error(error);
  }
}
